/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `virtualAppointmentProposal` for the Caintra Connect frontend.
 * @module logic/appointments/virtualAppointmentProposal
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
const buildVirtualProposalPayload = (virtualMeetingData) => ({
  subject: virtualMeetingData.subject,
  startDateTime: virtualMeetingData.startDateTime,
  endDateTime: virtualMeetingData.endDateTime,
  organizerUserId: virtualMeetingData.organizerUserId,
  attendeeUserId: virtualMeetingData.attendeeUserId,
});

/**
 * Proposes converting an appointment to a virtual meeting.
 * @returns {Promise<Object>} Updated appointment payload from API
 */
export const proposeVirtualAppointment = async ({
  eventId,
  appointmentId,
  virtualMeetingData,
}) => {
  const response = await fetch(
    `${process.env.REACT_APP_ROOT_API}appointment/${eventId}/${appointmentId}/virtual/propose`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        proposalData: buildVirtualProposalPayload(virtualMeetingData),
      }),
    },
  );

  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || "Virtual appointment proposal failed");
  }

  return result.data.appointment;
};
